import { Box, IconButton, styled } from '@mui/material';
import Done from '@mui/icons-material/Done';
import Close from '@mui/icons-material/Close';
import Download from '@mui/icons-material/Download';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { ImageFormField } from 'lib/form/fields/ImageFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { useDownloadAsset } from 'lib/hooks/useDownloadAsset';
import { SettingDisplay } from 'lib/SettingDisplay';

const PREFIX = 'PayAtTableGlobalSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`,
    image: `${PREFIX}-image`,
    patEmptyTableIcon: `${PREFIX}-patEmptyTableIcon`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    },
    [`& .${classes.image}`]: {
        width: 'auto',
        height: 'auto',
        maxHeight: '400px',
        maxWidth: '100%'
    },
    [`& .${classes.patEmptyTableIcon}`]: {
        width: 'auto',
        height: 'auto',
        maxHeight: '300px',
        maxWidth: '100%'
    }
}));

type IProps = {
    settings: IPublicAndPrivateSettings;
    tenantId: string;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export const PayAtTableGlobalSettings = ({ settings, tenantId, onSubmit }: IProps) => {
    const { loading: patSelectionImageLoading, handleDownload: handleDownloadPatSelectionImage } =
        useDownloadAsset({
            src: `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/web_pat_selection`,
            fileName: 'web_pat_selection'
        });

    const { loading: patEmptyTableIconLoading, handleDownload: handleDownloadPatEmptyTableIcon } =
        useDownloadAsset({
            src: `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/pat_empty_table_icon`,
            fileName: 'pat_empty_table_icon'
        });

    return (
        <StyledMuiCardForm
            title="Pay At Table"
            initialValues={{
                payAtTableEnabled: settings?.payAtTableEnabled ?? false,
                nfcEnabled: settings?.payAtTable?.nfcEnabled ?? false,
                limitLocationRange: settings?.payAtTable?.limitLocationRange ?? false,
                canChangeTableNumber: settings?.payAtTable?.canChangeTableNumber ?? true,
                patCardImage: `${
                    import.meta.env.MEDIA_SERVICE_URL
                }/tenants/${tenantId}/app_assets/web_pat_selection`,
                patEmptyTableIcon: `${
                    import.meta.env.MEDIA_SERVICE_URL
                }/tenants/${tenantId}/app_assets/pat_empty_table_icon`
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Pay At Table Enabled">
                        {settings?.payAtTableEnabled && <Done />}
                        {!settings?.payAtTableEnabled && <Close />}
                    </SettingDisplay>
                    <SettingDisplay label="Limit Location Range">
                        {settings?.payAtTable?.limitLocationRange && <Done />}
                        {!settings?.payAtTable?.limitLocationRange && <Close />}
                    </SettingDisplay>
                    <SettingDisplay label="Pay At Table NFC Enabled">
                        {settings?.payAtTable?.nfcEnabled && <Done />}
                        {!settings?.payAtTable?.nfcEnabled && <Close />}
                    </SettingDisplay>
                    <SettingDisplay label="Change Table Number Enabled">
                        {settings?.payAtTable?.canChangeTableNumber && <Done />}
                        {!settings?.payAtTable?.canChangeTableNumber && <Close />}
                    </SettingDisplay>
                    <SettingDisplay
                        label="Web Journey Selection Image"
                        description="Min Ratio 657px X 220px, Max Ratio 800px x 268px PNG or JPEG"
                    >
                        <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center" gap={1}>
                            <img
                                src={`${
                                    import.meta.env.MEDIA_SERVICE_URL
                                }/tenants/${tenantId}/app_assets/web_pat_selection`}
                                className={classes.image}
                            />
                            <IconButton
                                disabled={patSelectionImageLoading}
                                onClick={handleDownloadPatSelectionImage}
                            >
                                <Download />
                            </IconButton>
                        </Box>
                    </SettingDisplay>
                    <SettingDisplay
                        label="Empty Table Icon"
                        description="300px by 300px PNG or JPEG. If missing, it will use the cloche icon"
                        buildtimeIOS
                    >
                        <Box display="flex" flexDirection="row" flexWrap="wrap" alignItems="center" gap={1}>
                            <img
                                src={`${
                                    import.meta.env.MEDIA_SERVICE_URL
                                }/tenants/${tenantId}/app_assets/pat_empty_table_icon`}
                                className={classes.patEmptyTableIcon}
                            />
                            <IconButton
                                disabled={patEmptyTableIconLoading}
                                onClick={handleDownloadPatEmptyTableIcon}
                            >
                                <Download />
                            </IconButton>
                        </Box>
                    </SettingDisplay>
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay label="Pay At Table Enabled">
                        <SwitchFormField name="payAtTableEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Limit Location Range">
                        <SwitchFormField name="limitLocationRange" />
                    </SettingDisplay>
                    <SettingDisplay label="Pay At Table NFC Enabled">
                        <SwitchFormField name="nfcEnabled" />
                    </SettingDisplay>
                    <SettingDisplay label="Change Table Number Enabled">
                        <SwitchFormField name="canChangeTableNumber" />
                    </SettingDisplay>
                    <SettingDisplay
                        label="Web Journey Selection Image"
                        description="Min Ratio 657px X 220px, Max Ratio 800px x 268px PNG or JPEG"
                    >
                        <ImageFormField
                            name="patCardImage"
                            aspect={657 / 220}
                            fileName="web_pat_selection"
                            uploadFolder="app_assets"
                            unremovable
                        />
                    </SettingDisplay>
                    <SettingDisplay
                        label="Empty Table Icon"
                        description="300px by 300px PNG or JPEG. If missing, it will use the cloche icon"
                        buildtimeIOS
                    >
                        <ImageFormField
                            name="patEmptyTableIcon"
                            aspect={1}
                            fileName="pat_empty_table_icon"
                            uploadFolder="app_assets"
                            accept="image/png,image/jpeg"
                            className={classes.patEmptyTableIcon}
                        />
                    </SettingDisplay>
                </Box>
            }
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
};
